export type ElementViewabilityOptions = {
  minimumVisibleRatio?: number;
  sampleYPositions?: number[];
};

const DEFAULT_MINIMUM_VISIBLE_RATIO = 0.5;
const DEFAULT_SAMPLE_Y_POSITIONS = [0.1, 0.3, 0.5, 0.7, 0.9];

export function isElementViewable(
  element: HTMLElement,
  options: ElementViewabilityOptions = {}
): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const minimumVisibleRatio =
    options.minimumVisibleRatio ?? DEFAULT_MINIMUM_VISIBLE_RATIO;
  const sampleYPositions =
    options.sampleYPositions ?? DEFAULT_SAMPLE_Y_POSITIONS;

  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;

  const style = window.getComputedStyle(element);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    Number(style.opacity) === 0
  ) {
    return false;
  }

  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;
  if (viewportWidth <= 0 || viewportHeight <= 0) return false;

  const visibleLeft = Math.max(0, rect.left);
  const visibleTop = Math.max(0, rect.top);
  const visibleRight = Math.min(viewportWidth, rect.right);
  const visibleBottom = Math.min(viewportHeight, rect.bottom);
  const visibleWidth = Math.max(0, visibleRight - visibleLeft);
  const visibleHeight = Math.max(0, visibleBottom - visibleTop);
  const geometricVisibleRatio =
    (visibleWidth * visibleHeight) / (rect.width * rect.height);

  if (geometricVisibleRatio < minimumVisibleRatio) {
    return false;
  }

  const sampleX = visibleLeft + visibleWidth / 2;
  let visibleSamples = 0;

  for (const yPosition of sampleYPositions) {
    const sampleY = visibleTop + visibleHeight * yPosition;
    const x = Math.min(viewportWidth - 1, Math.max(0, sampleX));
    const y = Math.min(viewportHeight - 1, Math.max(0, sampleY));
    const topElement = document.elementFromPoint(x, y);

    if (topElement && element.contains(topElement)) {
      visibleSamples += 1;
    }
  }

  return visibleSamples / sampleYPositions.length >= minimumVisibleRatio;
}
